/**
 * @file app/helpers/vatSummary.js
 *
 * Pure, dependency-free VAT / ÁFA breakdown pipeline shared by every receipt
 * surface: the Android/RawBT builder (`oldPrinter.js` via `FiscatQRService`),
 * the Tauri/desktop ESC/POS renderer (`app/tauri/escposRenderer.js`), and the
 * Fiscat fiscal QR string.
 *
 * It intentionally imports ONLY the pure `orderVatResolver` helpers — no
 * `react-native`, `i18n`, `AsyncStorage`, Sentry or dialog modules — so it can
 * run inside the RN app, the Tauri desktop build, and the isolated pure-JS Jest
 * suite alike. Telemetry is injected by the caller through the optional `onInfo`
 * callback rather than importing a reporting service.
 *
 * Pricing rule: every Fiscat item row and the line gross are computed from the
 * actually-charged `remotePrice` (per-item discounts already baked in), NOT the
 * list `item.price` — the same as the Windows POS `generateQRCodeString`.
 */

import { normalizeVatRate, resolveLineVatRate, resolveVatOrderType } from './orderVatResolver';

/**
 * Map a numeric VAT percentage to its Fiscat `AEE|SLD` VAT group code.
 *
 * Must stay numeric (1/2/3/5) — identical to the Windows POS
 * (`Happy-POS/src/main-app.js` `generateQRCodeString`) and the Tauri path
 * (`app/tauri/cashDrawer.js`). The SAM4S `A00/B00/C00` codes make the Fiscat
 * register reject the whole receipt with "adathiba" (data error).
 *
 * @param {number} vatPercentage Numeric VAT rate (e.g. 27, 18, 5, 0).
 * @returns {number} Fiscat VAT group code (unknown rates fall back to 27% → 3).
 */
export const getVATCode = (vatPercentage) => {
    if (vatPercentage === 5) return 1;
    if (vatPercentage === 18) return 2;
    if (vatPercentage === 27) return 3;
    if (vatPercentage === 0) return 5;

    return 3;
};

/**
 * Round a monetary value to 2 decimals, guarding NaN.
 *
 * @param {number} value Raw amount.
 * @returns {number} Rounded amount (0 when not finite).
 */
export const roundMoney = (value) => {
    const normalized = Number(value);
    if (!Number.isFinite(normalized)) {
        return 0;
    }

    return Math.round((normalized + Number.EPSILON) * 100) / 100;
};

/**
 * Apply a Fiscat discount marker to a line's base gross.
 *
 * @param {number} baseGross Line gross before discount.
 * @param {string} discount Marker: `Mxx` (percent) or `-N` (absolute, negative).
 * @returns {number} Discounted gross.
 */
export const applyLineDiscount = (baseGross = 0, discount = '') => {
    const safeBaseGross = Number(baseGross) || 0;
    const normalizedDiscount = String(discount || '').trim();

    if (!normalizedDiscount) {
        return safeBaseGross;
    }

    if (normalizedDiscount.startsWith('M')) {
        const percent = Number(normalizedDiscount.slice(1));
        if (Number.isFinite(percent) && percent > 0) {
            return safeBaseGross - (safeBaseGross * percent) / 100;
        }

        return safeBaseGross;
    }

    if (normalizedDiscount.startsWith('-')) {
        const amount = Number(normalizedDiscount);
        if (Number.isFinite(amount)) {
            return safeBaseGross + amount;
        }
    }

    return safeBaseGross;
};

/**
 * Print order of the VAT breakdown buckets: products first (ascending rate),
 * then service-fee (`Sz…`) rows, then delivery/other fee rows — mirrors the
 * Windows POS receipt layout (5%, 27%, Sz5%, Sz27%, …).
 */
const FEE_TYPE_ORDER = { product: 0, service: 1, delivery: 2 };

/**
 * Build the printed row label for a VAT bucket. Product buckets render as the
 * bare rate (`27`); service-fee buckets carry the Windows `Sz` prefix (`Sz27`)
 * so the receipt shows a distinct szervízdíj line per VAT rate. The `%` sign is
 * appended by the renderers.
 *
 * @param {number} vatRate Numeric VAT rate.
 * @param {'product'|'service'|'delivery'} [feeType] Bucket kind.
 * @returns {string} Row label without the trailing `%`.
 */
export const vatRowLabel = (vatRate, feeType = 'product') => {
    return feeType === 'service' ? `Sz${vatRate}` : `${vatRate}`;
};

/**
 * On-screen label for a VAT breakdown row. Product rows show the bare rate
 * (`27%`); service-fee rows show the localized szervízdíj label
 * (`Szervízdíj (27%)`) so they never read as a duplicate product rate.
 *
 * @param {{vatRate:number, feeType?:string}} row Breakdown row from {@link getVatSummary}.
 * @param {(key:string, options?:object)=>string} [translate] i18n `t`; without it service rows fall back to `Sz<rate>%`.
 * @returns {string} Display label.
 */
export const vatRowDisplayLabel = (row = {}, translate) => {
    const vatRate = row?.vatRate ?? 0;
    if (row?.feeType !== 'service') {
        return `${vatRate}%`;
    }

    return typeof translate === 'function'
        ? translate('fiscat_service_fee_label', { vat: vatRate })
        : `${vatRowLabel(vatRate, 'service')}%`;
};

/**
 * Stable sort comparator for breakdown rows: fee-type group first, then rate.
 *
 * @param {{vatRate:number, feeType?:string}} a First row.
 * @param {{vatRate:number, feeType?:string}} b Second row.
 * @returns {number} Sort order.
 */
export const sortVatRows = (a = {}, b = {}) => {
    const orderA = FEE_TYPE_ORDER[a?.feeType] ?? 0;
    const orderB = FEE_TYPE_ORDER[b?.feeType] ?? 0;
    if (orderA !== orderB) {
        return orderA - orderB;
    }

    return Number(a?.vatRate) - Number(b?.vatRate);
};

/**
 * Aggregate sale rows into a normalized VAT breakdown array.
 *
 * Prefers each row's pre-computed `grossBase` (the VAT-relevant gross derived
 * from `remotePrice`); otherwise falls back to `unitPrice * quantity` minus the
 * row's discount marker. Service-fee rows (`feeType: 'service'`) are bucketed
 * separately from product rows of the same rate so the receipt renders distinct
 * `Sz<rate>%` lines, matching the Windows POS receipt.
 *
 * @param {Array<object>} saleRows Rows from {@link buildSaleData}.
 * @returns {Array<{vatRate:number, feeType:string, label:string, net:number, vat:number, gross:number}>} Sorted breakdown.
 */
export const calculateVatSummary = (saleRows = []) => {
    const vatSummaryByKey = {};

    saleRows.forEach((row = {}) => {
        const vatRate = normalizeVatRate(row?.vatRate);
        const feeType = row?.feeType || 'product';
        const quantity = Number(row?.quantity || 1);
        const unitPrice = Number(row?.unitPrice || 0);
        // Prefer the pre-computed VAT-relevant gross (derived from `remotePrice`)
        // when the row carries one; otherwise fall back to list `unitPrice`
        // minus its discount marker.
        const hasGrossBase = row?.grossBase !== null && row?.grossBase !== undefined && Number.isFinite(Number(row?.grossBase));
        const gross = hasGrossBase
            ? Number(row.grossBase)
            : applyLineDiscount(unitPrice * quantity, row?.discount);
        const net = vatRate === 0 ? gross : gross / (vatRate / 100 + 1);
        const vat = gross - net;
        const bucketKey = `${feeType}|${vatRate}`;

        if (!vatSummaryByKey[bucketKey]) {
            vatSummaryByKey[bucketKey] = {
                vatRate,
                feeType,
                label: vatRowLabel(vatRate, feeType),
                net: 0,
                vat: 0,
                gross: 0,
            };
        }

        vatSummaryByKey[bucketKey].net += net;
        vatSummaryByKey[bucketKey].vat += vat;
        vatSummaryByKey[bucketKey].gross += gross;
    });

    return Object.values(vatSummaryByKey)
        .map((summaryRow) => ({
            ...summaryRow,
            net: roundMoney(summaryRow.net),
            vat: roundMoney(summaryRow.vat),
            gross: roundMoney(summaryRow.gross),
        }))
        .sort(sortVatRows);
};

/**
 * Normalize persisted `{ vat, net, gross }` rate→amount maps into the array shape.
 *
 * Persisted maps may carry Windows-style service-fee keys (`Sz5`, `Sz27`,
 * `Szervízdíj27`) alongside the numeric product-rate keys. Those are preserved
 * as separate service-fee rows (`feeType: 'service'`, label `Sz<rate>`) so the
 * printed breakdown shows a distinct szervízdíj line per VAT rate rather than
 * dropping the service fee from the receipt.
 *
 * @param {{vat?:object, net?:object, gross?:object}} summaryMaps Persisted maps.
 * @returns {Array<{vatRate:number, feeType:string, label:string, net:number, vat:number, gross:number}>} Sorted breakdown.
 */
export const normalizePersistedVatSummary = (summaryMaps = {}) => {
    const vatMap = summaryMaps?.vat || {};
    const netMap = summaryMaps?.net || {};
    const grossMap = summaryMaps?.gross || {};
    const vatKeys = new Set([
        ...Object.keys(vatMap || {}),
        ...Object.keys(netMap || {}),
        ...Object.keys(grossMap || {}),
    ]);

    return [...vatKeys]
        .map((vatKey) => {
            const rawKey = String(vatKey);
            const isServiceFee = /^Sz/i.test(rawKey);
            // Extract the numeric VAT rate from either a bare rate (`27`) or a
            // service-fee key (`Sz27` / `Szervízdíj27`).
            const numericMatch = rawKey.match(/-?\d+(?:\.\d+)?/);
            const numericVatRate = numericMatch ? Number(numericMatch[0]) : NaN;
            if (!Number.isFinite(numericVatRate)) {
                return null;
            }

            const feeType = isServiceFee ? 'service' : 'product';

            return {
                vatRate: numericVatRate,
                feeType,
                label: vatRowLabel(numericVatRate, feeType),
                net: roundMoney(netMap?.[vatKey] || 0),
                vat: roundMoney(vatMap?.[vatKey] || 0),
                gross: roundMoney(grossMap?.[vatKey] || 0),
            };
        })
        .filter(Boolean)
        .sort(sortVatRows);
};

/**
 * Resolve a persisted VAT breakdown already attached to the order, if any.
 *
 * @param {object} order Order object.
 * @returns {Array<{vatRate:number, net:number, vat:number, gross:number}>} Breakdown, or `[]`.
 */
export const getPersistedVatSummary = (order = {}) => {
    const legacySummary = order?.vatSummary && typeof order?.vatSummary === 'object' && !Array.isArray(order?.vatSummary)
        ? order.vatSummary
        : null;

    const hasDirectSummaryMaps = [order?.vatSummary, order?.netSummary, order?.grossSummary].some((value) => {
        return value && typeof value === 'object' && !Array.isArray(value);
    });

    if (hasDirectSummaryMaps && !(legacySummary?.vat || legacySummary?.net || legacySummary?.gross)) {
        const normalizedDirectSummary = normalizePersistedVatSummary({
            vat: order?.vatSummary,
            net: order?.netSummary,
            gross: order?.grossSummary,
        });

        if (normalizedDirectSummary.length > 0) {
            return normalizedDirectSummary;
        }
    }

    if (legacySummary?.vat || legacySummary?.net || legacySummary?.gross) {
        const normalizedLegacySummary = normalizePersistedVatSummary({
            vat: legacySummary?.vat,
            net: legacySummary?.net,
            gross: legacySummary?.gross,
        });

        if (normalizedLegacySummary.length > 0) {
            return normalizedLegacySummary;
        }
    }

    return [];
};

/**
 * Whether a service fee applies to this order (dine-in only).
 *
 * @param {object} order Order object.
 * @returns {boolean} True for dine-in orders.
 */
export const shouldApplyServiceFee = (order = {}) => {
    return resolveVatOrderType(order) === 'dineIn';
};

/**
 * Coerce a value to a finite number (0 when not parseable).
 *
 * @param {*} value Raw value.
 * @returns {number} Finite number.
 */
const toFiniteAmount = (value) => {
    const normalized = Number(value);

    return Number.isFinite(normalized) ? normalized : 0;
};

/**
 * Build the order's service-fee fields in the backend/sync contract: `serviceFee`
 * is the restaurant's **percentage**, `serviceFeePrice` the charged **HUF amount**
 * (see `SyncManager` `serviceFee`/`serviceFeePrice`). The percentage is only set
 * when a fee was actually charged, so a place with `showServiceFee` off never
 * gets a fee re-derived from its configured rate.
 *
 * @param {number} serviceFeeAmount Charged service fee in HUF (`CartContext.serviceFeeAmount`).
 * @param {number} restaurantServiceFeePercent The place's configured `serviceFee` percentage.
 * @returns {{serviceFee:number, serviceFeePrice:number}} Order fields.
 */
export const buildOrderServiceFeeFields = (serviceFeeAmount = 0, restaurantServiceFeePercent = 0) => {
    const serviceFeePrice = Math.max(0, Math.round(toFiniteAmount(serviceFeeAmount)));
    if (serviceFeePrice <= 0) {
        return { serviceFee: 0, serviceFeePrice: 0 };
    }

    const percent = toFiniteAmount(restaurantServiceFeePercent);

    return {
        serviceFee: percent > 0 && percent <= 100 ? percent : 0,
        serviceFeePrice,
    };
};

/**
 * Resolve the actually-charged **unit** price of an order line — the Fiscat row
 * price, identical to the Windows POS `item.remotePrice || item.price`.
 *
 * services-app stores `remotePrice` in two shapes: server / already-sent lines
 * carry it per unit, while unsent cart lines carry the discounted **line total**
 * (`price × qty − per-item discount`, written by `CartContext.addDiscount`,
 * `CartScreen` ±quantity and `DiscountModal`). A line total is recognised by
 * matching exactly that formula and divided back to a unit price.
 *
 * @param {{price?:number, remotePrice?:number, discounts?:{amount?:number, percentage?:number}}} item Order line.
 * @param {number} [quantity=1] Line quantity (`amount || quantity`).
 * @returns {number} Charged unit price (unrounded; list `price` when no `remotePrice`).
 */
export const resolveChargedUnitPrice = (item, quantity = 1) => {
    const listPrice = toFiniteAmount(item?.price);
    const remotePrice = toFiniteAmount(item?.remotePrice);
    if (remotePrice <= 0) {
        return listPrice;
    }

    const qty = toFiniteAmount(quantity) > 0 ? toFiniteAmount(quantity) : 1;
    if (qty > 1) {
        const lineGross = listPrice * qty;
        const discountAmount = toFiniteAmount(item?.discounts?.amount);
        const percentAmount = lineGross * (toFiniteAmount(item?.discounts?.percentage) / 100);
        // `CartContext.addDiscount` subtracts both parts; `CartScreen` ±quantity only one.
        const cartLineTotals = [
            lineGross - discountAmount - percentAmount,
            lineGross - discountAmount,
            lineGross - percentAmount,
        ];
        if (cartLineTotals.some((lineTotal) => Math.abs(remotePrice - lineTotal) < 0.01)) {
            return remotePrice / qty;
        }
    }

    return remotePrice;
};

/**
 * Distribute a total amount proportionally across the per-VAT gross buckets,
 * assigning any rounding remainder to the last bucket so the parts sum exactly
 * to `total`.
 *
 * @param {number} total Amount to distribute.
 * @param {Object<string, number>} grossByVat Gross per VAT rate.
 * @returns {Object<number, number>} Amount per (normalized) VAT rate.
 */
const distributeAmountByVat = (total, grossByVat = {}) => {
    const roundedTotal = Math.round(toFiniteAmount(total));
    const vatKeys = Object.keys(grossByVat || {});
    const base = vatKeys.reduce((sum, key) => sum + toFiniteAmount(grossByVat[key]), 0);
    if (roundedTotal <= 0 || base <= 0) {
        return {};
    }

    const distributed = {};
    let allocated = 0;
    vatKeys.forEach((key, index) => {
        const vatRate = normalizeVatRate(String(key).replace(/^Sz/i, ''));
        const bucket = index === vatKeys.length - 1
            ? roundedTotal - allocated
            : Math.round((toFiniteAmount(grossByVat[key]) / base) * roundedTotal);
        if (bucket > 0) {
            distributed[vatRate] = (distributed[vatRate] || 0) + bucket;
            allocated += bucket;
        }
    });

    return distributed;
};

/**
 * Resolve the service-fee gross amount per VAT rate for an order, in priority
 * order:
 *   1. `grossSummary`/`grossSum` keys prefixed `Sz` (Windows already split them);
 *   2. an explicit `serviceFeePrice` total, distributed across the product VAT
 *      buckets so both `Sz5`/`Sz27` lines appear (Windows parity);
 *   3. the `serviceFee` percentage applied to the product VAT buckets.
 *
 * @param {object} order Order object.
 * @param {Object<string, number>} itemTotalsByVat Product gross per VAT rate.
 * @param {(message:string, meta?:object, context?:string)=>void} [onInfo] Telemetry sink.
 * @returns {Object<number, number>} Service-fee gross per VAT rate.
 */
export const resolveServiceFeeGrossByVat = (order = {}, itemTotalsByVat = {}, onInfo = () => {}) => {
    const grossSum = order?.grossSummary && typeof order.grossSummary === 'object'
        ? order.grossSummary
        : (order?.grossSum && typeof order.grossSum === 'object' ? order.grossSum : null);
    const serviceFeeFromGrossSum = {};

    if (grossSum) {
        Object.keys(grossSum).forEach((key) => {
            if (!/^Sz/i.test(String(key))) return;
            const vatRate = normalizeVatRate(String(key).replace(/^Sz/i, ''));
            const amount = Math.round(toFiniteAmount(grossSum[key]));
            if (amount > 0) {
                serviceFeeFromGrossSum[vatRate] = (serviceFeeFromGrossSum[vatRate] || 0) + amount;
            }
        });
    }

    if (Object.keys(serviceFeeFromGrossSum).length > 0) {
        onInfo('Service fee source: grossSum Sz*', {
            orderCode: order?.orderCode,
            serviceFee: serviceFeeFromGrossSum,
        }, 'vatSummary.resolveServiceFeeGrossByVat');

        return serviceFeeFromGrossSum;
    }

    const explicitServiceFee = Math.round(toFiniteAmount(order?.serviceFeePrice));
    if (explicitServiceFee > 0) {
        const distributed = distributeAmountByVat(explicitServiceFee, itemTotalsByVat);
        if (Object.keys(distributed).length > 0) {
            onInfo('Service fee source: serviceFeePrice (distributed)', {
                orderCode: order?.orderCode,
                serviceFeePrice: explicitServiceFee,
                serviceFee: distributed,
            }, 'vatSummary.resolveServiceFeeGrossByVat');

            return distributed;
        }

        // No product VAT buckets to distribute over — fall back to the dominant
        // rate (or 27%) so the fee still lands on the receipt.
        const buckets = Object.entries(itemTotalsByVat).sort((a, b) => Number(b[1]) - Number(a[1]));
        const dominantVat = buckets.length > 0 ? normalizeVatRate(buckets[0][0]) : 27;

        return { [dominantVat]: explicitServiceFee };
    }

    const feePercent = toFiniteAmount(order?.serviceFee);
    if (feePercent <= 0 || feePercent > 100) {
        return {};
    }

    const vatSum = Object.keys(itemTotalsByVat).reduce((sum, key) => sum + toFiniteAmount(itemTotalsByVat[key]), 0);
    if (vatSum <= 0) {
        return {};
    }

    const distributed = distributeAmountByVat(Math.round((vatSum * feePercent) / 100), itemTotalsByVat);
    onInfo('Service fee source: percentage fallback', {
        orderCode: order?.orderCode,
        serviceFeePercent: feePercent,
        serviceFee: distributed,
    }, 'vatSummary.resolveServiceFeeGrossByVat');

    return distributed;
};

/**
 * Build the service-fee VAT breakdown rows for an order, given the product-side
 * gross per VAT rate. Used to graft `Sz<rate>%` rows onto a persisted
 * product-only summary that omitted the service fee.
 *
 * @param {object} order Order object.
 * @param {Array<{vatRate:number, gross:number}>} productRows Product breakdown rows.
 * @param {(message:string, meta?:object, context?:string)=>void} [onInfo] Telemetry sink.
 * @returns {Array<{vatRate:number, feeType:string, label:string, net:number, vat:number, gross:number}>} Service-fee rows.
 */
export const computeServiceFeeVatRows = (order = {}, productRows = [], onInfo = () => {}) => {
    if (!shouldApplyServiceFee(order) || !(order?.serviceFee > 0 || order?.serviceFeePrice > 0)) {
        return [];
    }

    const itemTotalsByVat = {};
    (Array.isArray(productRows) ? productRows : []).forEach((row) => {
        const vatRate = normalizeVatRate(row?.vatRate);
        itemTotalsByVat[vatRate] = (itemTotalsByVat[vatRate] || 0) + toFiniteAmount(row?.gross);
    });

    const serviceFeeGrossByVat = resolveServiceFeeGrossByVat(order, itemTotalsByVat, onInfo);

    return Object.keys(serviceFeeGrossByVat)
        .map((vatKey) => {
            const vatRate = normalizeVatRate(vatKey);
            const gross = Math.round(toFiniteAmount(serviceFeeGrossByVat[vatKey]));
            if (gross <= 0) {
                return null;
            }

            const net = vatRate === 0 ? gross : gross / (vatRate / 100 + 1);

            return {
                vatRate,
                feeType: 'service',
                label: vatRowLabel(vatRate, 'service'),
                net: roundMoney(net),
                vat: roundMoney(gross - net),
                gross: roundMoney(gross),
            };
        })
        .filter(Boolean)
        .sort(sortVatRows);
};

/**
 * Build Fiscat sale data (QR string, sale rows, VAT breakdown) from an order.
 *
 * Item and extra rows are priced at the charged unit price (`remotePrice`, see
 * {@link resolveChargedUnitPrice}) like the Windows POS; the only per-row
 * discount marker is the order-level `M<pct>`.
 *
 * @param {object} order Order object with `items`.
 * @param {{onInfo?: (message:string, meta?:object, context?:string)=>void}} [options] Optional telemetry sink.
 * @returns {{rowsCount:number, qrCodeString:string, saleRows:Array<object>, vatSummary:Array<object>, totals:object}|null}
 *   Sale data, or `null` for empty/zero orders. `totals` is the receipt summary
 *   charged by these rows (see {@link computeReceiptTotals}).
 */
export const buildSaleData = (order, options = {}) => {
    const onInfo = typeof options?.onInfo === 'function' ? options.onInfo : () => {};

    if (!order) {
        return null;
    }

    const {orderCode, items} = order;

    if (!items || items.length === 0) {
        onInfo("Empty order detected, skipping QR code generation", {orderCode}, "vatSummary.buildSaleData");

        return null;
    }

    const hasNonZeroPrice = items.some((item) => {
        const resolvedPrice = Number(item.remotePrice ?? item.price ?? 0);

        return resolvedPrice !== 0;
    });

    if (!hasNonZeroPrice) {
        onInfo("Order with zero price detected, skipping QR code generation", {orderCode}, "vatSummary.buildSaleData");

        return null;
    }

    let qrCodeString = '';
    let rowsCount = 0;
    const saleRows = [];
    const discountPercentage = Math.round(order?.discounts?.percentage || order?.discount?.percentage || 0);
    const serviceFees = {};
    const itemTotalsByVat = {};
    // Receipt totals, accumulated from exactly the rows sent to the register.
    let productsGross = 0;
    let productsDiscountedGross = 0;
    let serviceFeeTotal = 0;

    const toAmount = (value) => {
        const normalized = Number(value);

        return Number.isFinite(normalized) ? normalized : 0;
    };

    const addVatTotal = (vatRate, amount) => {
        const normalizedVat = normalizeVatRate(vatRate);
        const normalizedAmount = toAmount(amount);
        if (normalizedAmount <= 0) return;
        itemTotalsByVat[normalizedVat] = (itemTotalsByVat[normalizedVat] || 0) + normalizedAmount;
    };

    const addSaleRow = ({name, vatRate, unitPrice, quantity = 1, discount = '', grossBase = null, feeType = 'product'}) => {
        const normalizedVatRate = normalizeVatRate(vatRate);
        const normalizedUnitPrice = Math.round(Number(unitPrice) || 0);
        const normalizedQuantity = Number(quantity || 1) || 1;
        const normalizedDiscount = discount || '';
        // `grossBase`, when provided, is the VAT-relevant charged gross used by the
        // printed ÁFA breakdown; product rows already carry the charged unit price.
        const normalizedGrossBase = Number.isFinite(Number(grossBase)) && grossBase !== null
            ? roundMoney(grossBase)
            : null;

        saleRows.push({
            name,
            vatRate: normalizedVatRate,
            unitPrice: normalizedUnitPrice,
            quantity: normalizedQuantity,
            discount: normalizedDiscount,
            grossBase: normalizedGrossBase,
            feeType,
        });

        rowsCount++;
        qrCodeString += `${name}|${getVATCode(normalizedVatRate)}|${normalizedUnitPrice}|${normalizedQuantity}|${normalizedDiscount}|`;
    };

    /**
     * Emit a charged line at its `remotePrice`-based unit price. Fiscat prices are
     * whole HUF, so a fractional unit price (a discounted line total not divisible
     * by the quantity) is split into two rows that add up to the exact line total.
     */
    const addChargedRows = ({name, vatRate, unitPrice, quantity, discount}) => {
        const lineTotal = Math.round(toAmount(unitPrice) * quantity);
        addVatTotal(vatRate, lineTotal);
        productsGross += lineTotal;
        productsDiscountedGross += applyLineDiscount(lineTotal, discount);

        const baseUnit = Math.floor(lineTotal / quantity);
        const remainder = lineTotal - (baseUnit * quantity);
        if (!Number.isInteger(quantity) || remainder <= 0) {
            addSaleRow({name, vatRate, unitPrice, quantity, discount, grossBase: applyLineDiscount(lineTotal, discount)});

            return;
        }

        const baseQuantity = quantity - remainder;
        addSaleRow({name, vatRate, unitPrice: baseUnit, quantity: baseQuantity, discount, grossBase: applyLineDiscount(baseUnit * baseQuantity, discount)});
        addSaleRow({name, vatRate, unitPrice: baseUnit + 1, quantity: remainder, discount, grossBase: applyLineDiscount((baseUnit + 1) * remainder, discount)});
    };

    ([...items]).forEach((item) => {
        const itemExtras = Array.isArray(item?.extras) ? item.extras : [];
        const itemName = item.remoteName || item.name?.hu || item.name || 'GYŰJTŐ';
        const vatPercentage = resolveLineVatRate(item, order);
        const itemQuantity = item.amount || item.quantity || 1;
        // Order-level percentage applies on top of the already-reduced item prices
        // (`CartContext.totalDiscountPercentage`) — every row, as in the Windows POS.
        const orderPercentStr = discountPercentage > 0 ? `M${discountPercentage}` : '';

        addChargedRows({
            name: itemName,
            vatRate: vatPercentage,
            unitPrice: resolveChargedUnitPrice(item, itemQuantity),
            quantity: itemQuantity,
            discount: orderPercentStr,
        });

        itemExtras.forEach((extra) => {
            const extraPrice = toAmount(extra.remotePrice || extra.price);
            if (extraPrice > 0) {
                const extraName = extra.remoteName || extra?.item?.name?.hu || 'GYŰJTŐ';
                addChargedRows({
                    name: ` +${extraName}`,
                    vatRate: resolveLineVatRate(extra, order, vatPercentage),
                    unitPrice: extraPrice,
                    quantity: itemQuantity,
                    discount: orderPercentStr,
                });
            }
        });
    });

    if (shouldApplyServiceFee(order) && (order.serviceFee > 0 || order.serviceFeePrice > 0)) {
        const resolvedServiceFees = resolveServiceFeeGrossByVat(order, itemTotalsByVat, onInfo);
        Object.keys(resolvedServiceFees).forEach((vatRate) => {
            serviceFees[vatRate] = resolvedServiceFees[vatRate];
        });
    }

    const discountAmount = Math.round(order.discounts?.amount || order.discount?.amount || order.discountAmount || order.discountPrice || 0);
    if (discountAmount > 0 && !discountPercentage) {
        addSaleRow({
            name: 'Kedvezmény',
            vatRate: 27,
            unitPrice: discountAmount * -1,
            quantity: 1,
        });
    }

    if (shouldApplyServiceFee(order) && (order.serviceFee > 0 || order.serviceFeePrice > 0)) {
        Object.keys(serviceFees).forEach((vatRate) => {
            const serviceFeeAmount = Math.round(toAmount(serviceFees[vatRate]));
            if (serviceFeeAmount > 0) {
                serviceFeeTotal += serviceFeeAmount;
                addSaleRow({
                    name: `Szervízdíj ${vatRate}%`,
                    vatRate: Number(vatRate),
                    unitPrice: serviceFeeAmount,
                    quantity: 1,
                    feeType: 'service',
                });
            }
        });
    }

    const deliveryFeeAmount = order.deliveryFee && order.deliveryFee > 0 ? Math.round(order.deliveryFee) : 0;
    if (deliveryFeeAmount > 0) {
        addSaleRow({
            name: 'Szállítási díj',
            vatRate: 27,
            unitPrice: deliveryFeeAmount,
            quantity: 1,
        });
    }

    const amountDiscount = discountAmount > 0 && !discountPercentage ? discountAmount : 0;
    const productsAmount = Math.round(productsGross);
    const total = Math.max(0, Math.round(productsDiscountedGross - amountDiscount + serviceFeeTotal + deliveryFeeAmount));
    const configuredServiceFeePercent = toAmount(order?.serviceFee);

    return {
        rowsCount,
        qrCodeString,
        saleRows,
        vatSummary: calculateVatSummary(saleRows),
        totals: {
            productsAmount,
            discountPercentage,
            // Derived from the rounded total so the printed rows always add up exactly.
            discountAmount: Math.max(0, productsAmount + serviceFeeTotal + deliveryFeeAmount - total),
            serviceFeeAmount: serviceFeeTotal,
            serviceFeePercent: serviceFeeTotal > 0
                ? (configuredServiceFeePercent > 0 && configuredServiceFeePercent <= 100
                    ? configuredServiceFeePercent
                    : (productsAmount > 0 ? roundMoney((serviceFeeTotal / productsAmount) * 100) : 0))
                : 0,
            deliveryFeeAmount,
            total,
        },
    };
};

/**
 * Resolve the totals printed on a customer receipt from the same charged rows the
 * Fiscat register receives ({@link buildSaleData}), so the nyugta and the fiscal
 * receipt can never disagree:
 *
 *   Termék(ek) összesen + Szervízdíj + Szállítási díj − Kedvezmény = Végösszeg
 *
 * `order.orderSum` is deliberately NOT used: the local post-payment dine-in order
 * (`paymentHelper.send_storeOrder`) takes it from `cartTotal`, which several
 * payment paths pass without the szervízdíj — deriving the products total as
 * `orderSum − serviceFeePrice` then silently dropped the fee from the receipt.
 *
 * @param {object} order Order with `items` (+ optional `discounts`, `serviceFee`
 *   percentage, `serviceFeePrice` HUF, `deliveryFee`, `orderType`).
 * @returns {{
 *   productsAmount:number,
 *   discountPercentage:number,
 *   discountAmount:number,
 *   serviceFeeAmount:number,
 *   serviceFeePercent:number,
 *   deliveryFeeAmount:number,
 *   total:number
 * }|null} Whole-HUF totals, or `null` when the order has no priced items (the
 *   caller then falls back to the persisted `orderSum`).
 */
export const computeReceiptTotals = (order) => {
    return buildSaleData(order)?.totals || null;
};

/**
 * Resolve the VAT breakdown for a receipt: prefer any persisted summary maps on
 * the order, else recompute from items via {@link buildSaleData} (remotePrice).
 *
 * @param {object} order Order object.
 * @param {{onInfo?: Function}} [options] Optional telemetry sink forwarded to {@link buildSaleData}.
 * @returns {Array<{vatRate:number, net:number, vat:number, gross:number}>} Normalized breakdown.
 */
export const getVatSummary = (order, options = {}) => {
    const onInfo = typeof options?.onInfo === 'function' ? options.onInfo : () => {};

    // Primary source of truth: recompute the breakdown from the order's own food
    // line items so every printed ÁFA table reflects each food's configured
    // `vat[orderType]` (dineIn/online/takeAway) and the actually-charged
    // `remotePrice`, rather than a possibly-stale persisted summary. `buildSaleData`
    // already folds in the szervízdíj (as `Sz<rate>%` rows) and delivery fee.
    if (Array.isArray(order?.items) && order.items.length > 0) {
        const computedVatSummary = buildSaleData(order, options)?.vatSummary || [];
        if (computedVatSummary.length > 0) {
            return computedVatSummary;
        }
    }

    // Fallback: orders that no longer carry resolvable item VAT config (offline /
    // historical / re-hydrated) still print from their persisted summary maps.
    const persistedVatSummary = getPersistedVatSummary(order);

    if (persistedVatSummary.length > 0) {
        const productRows = persistedVatSummary.filter((row) => row?.feeType !== 'service');
        const persistedServiceRows = persistedVatSummary.filter((row) => row?.feeType === 'service');

        // A persisted summary frequently carries only the product-rate buckets
        // (no `Sz*` keys). When the order still has a service fee, graft the
        // szervízdíj rows on so the printed breakdown matches the Windows POS
        // receipt (5%, 27%, Sz5%, Sz27%) instead of silently dropping the fee.
        const serviceRows = persistedServiceRows.length > 0
            ? persistedServiceRows
            : computeServiceFeeVatRows(order, productRows, onInfo);

        return [...productRows, ...serviceRows].sort(sortVatRows);
    }

    return buildSaleData(order, options)?.vatSummary || [];
};
